import { Fragment, useEffect, useState } from 'react';
import type { McpGrant, McpGrantsResponse, McpScope } from '../api/client.js';
import { fetchMcpGrants, revokeMcpGrant } from '../api/client.js';
import { Stamp } from '../components/Stamp.js';
import { useDictionary } from '../i18n/context.js';
import type { Dictionary, McpScopeKey } from '../i18n/types.js';

// ---------------------------------------------------------------------------
// AI connections (UI structure spec 16). Spec 16.1: "The MCP server is the
// primary content-creation path, so the authorizations held by AI clients
// are operational state the operator must be able to see and withdraw."
// This screen answers three questions -- where is the server, who is
// currently authorized, what can each of them do -- and lets the operator
// revoke one.
//
// 16.2's content order is load-bearing, not decorative: server endpoint
// first (an operator setting up a new client needs it immediately), then
// setup guidance, then the client list, then a scope reference. An
// operator arriving in an emergency needs Revoke reachable without
// scrolling past setup guidance meant for someone doing the opposite task.
//
// 16.6: `McpGrant.state` (apps/worker/src/mcp/grants.ts, mirrored in
// api/client.ts) is `'active' | 'revoked'` only -- there is no expiry
// column and grants do not expire on a timer, so this screen renders
// exactly those two states and never invents a third "expired" one.
// ---------------------------------------------------------------------------

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: McpGrantsResponse };

type RevokeState =
  | { status: 'idle' }
  | { status: 'confirming' }
  | { status: 'revoking' }
  | { status: 'error' };

const IDLE_REVOKE: RevokeState = { status: 'idle' };

// Every scope this deployment can grant (mirrors `MCP_SCOPES`,
// apps/worker/src/mcp/auth.ts -- this file has no compile-time link to the
// Worker, so the list is hand-kept in sync the same way every other wire
// type in api/client.ts is). Used only for the static scope-reference
// section (16.2's fourth block); a specific grant's own `scopes` and
// `writeScopes` -- both server-computed -- are what every other part of
// this screen renders.
const ALL_SCOPES: McpScope[] = [
  'projects:read', 'projects:write', 'campaigns:read', 'campaigns:write', 'media:write',
  'schedule:write', 'approval:write', 'publish:write', 'automation:write', 'activity:read',
];

const SCOPE_DICT_KEY: Record<McpScope, McpScopeKey> = {
  'projects:read': 'projectsRead',
  'projects:write': 'projectsWrite',
  'campaigns:read': 'campaignsRead',
  'campaigns:write': 'campaignsWrite',
  'media:write': 'mediaWrite',
  'schedule:write': 'scheduleWrite',
  'approval:write': 'approvalWrite',
  'publish:write': 'publishWrite',
  'automation:write': 'automationWrite',
  'activity:read': 'activityRead',
};

// 16.3 names two separate things this function computes, not one:
//
// 1. "Granted scopes, separated into read and write" -- a literal split on
//    each scope's own `:read`/`:write` suffix (`MCP_SCOPES`,
//    apps/worker/src/mcp/auth.ts, names every scope that way). `readScopes`
//    and `writeScopes` here are that split, straight from `grant.scopes`.
// 2. "Whether the grant includes approval, immediate publication, or
//    automation control, stated in plain language rather than scope names
//    alone" -- `capabilities`, derived only from the server-computed
//    `grant.writeScopes` (apps/worker/src/mcp/grants.ts's `toWireGrant`),
//    never from `grant.scopes` itself. This is deliberately a *different*
//    set from bullet 1's literal `writeScopes`: `campaigns:write` is a
//    "write" scope by name (bullet 1) but grants no unattended write
//    authority (it still waits for human review -- `MCP_WRITE_SCOPES`'s own
//    doc comment), so it never produces a capability here. The task brief's
//    rule -- "the frontend must not decide which scopes are dangerous" --
//    is why this half reads `grant.writeScopes` rather than recomputing
//    which scopes count as unattended itself.
//
// Exported and tested directly, the way `connectionView` in project.tsx is.
export type GrantCapability = 'approval' | 'publish' | 'automation';

export type GrantAuthority = {
  readScopes: McpScope[];
  writeScopes: McpScope[];
  capabilities: GrantCapability[];
};

export const grantAuthority = (grant: McpGrant): GrantAuthority => {
  const readScopes = grant.scopes.filter((scope) => scope.endsWith(':read'));
  const writeScopes = grant.scopes.filter((scope) => scope.endsWith(':write'));
  const writeSet = new Set(grant.writeScopes);
  const capabilities: GrantCapability[] = [];
  if (writeSet.has('approval:write')) capabilities.push('approval');
  if (writeSet.has('publish:write')) capabilities.push('publish');
  if (writeSet.has('automation:write')) capabilities.push('automation');
  return { readScopes, writeScopes, capabilities };
};

// 16.6: revoked grants stay visible for audit, "clearly separated from
// active grants" -- this is that separation, computed once rather than
// filtered twice inline. Exported and tested directly.
export type GrantBuckets = { active: McpGrant[]; revoked: McpGrant[] };

export const partitionGrants = (grants: McpGrant[]): GrantBuckets => ({
  active: grants.filter((g) => g.state === 'active'),
  revoked: grants.filter((g) => g.state === 'revoked'),
});

const capabilityLabel = (dict: Dictionary, capability: GrantCapability): string => {
  if (capability === 'approval') return dict.ui.aiConnections.capabilityApproval;
  if (capability === 'publish') return dict.ui.aiConnections.capabilityPublish;
  return dict.ui.aiConnections.capabilityAutomation;
};

const GrantRow = ({
  dict, grant, revokeState, onStartRevoke, onCancelRevoke, onConfirmRevoke,
}: {
  dict: Dictionary;
  grant: McpGrant;
  revokeState: RevokeState;
  onStartRevoke: (id: string) => void;
  onCancelRevoke: (id: string) => void;
  onConfirmRevoke: (id: string) => void;
}): React.JSX.Element => {
  const authority = grantAuthority(grant);
  const showConfirm = revokeState.status !== 'idle';

  return (
    <div className="panel" data-testid={`mcp-grant-${grant.id}`}>
      <div className="panel__body stack-2">
        <div className="between">
          {/* 16.5/15.2: client identity as presented during authorization,
              never labeled "AI" beyond what the client itself asserts. */}
          <span className="strong">{grant.client}</span>
          <span
            className={grant.state === 'active' ? 'chip chip--done' : 'chip'}
            data-testid={`mcp-grant-state-${grant.id}`}
          >
            {grant.state === 'active' ? dict.ui.aiConnections.stateActive : dict.ui.aiConnections.stateRevoked}
          </span>
        </div>

        <div className="row wrap" style={{ gap: 'var(--sp-5)' }}>
          <div className="stack-1">
            <span className="t-label">{dict.ui.aiConnections.authorizedAtLabel}</span>
            <Stamp instant={grant.authorizedAt} compact />
          </div>
          <div className="stack-1">
            <span className="t-label">{dict.ui.aiConnections.lastActivityLabel}</span>
            {grant.lastActivityAt
              ? <Stamp instant={grant.lastActivityAt} compact />
              : <span className="t-meta">{dict.ui.aiConnections.lastActivityNever}</span>}
          </div>
        </div>

        {/* 16.3 bullet 2: granted scopes, separated into read and write --
            the literal scope-name split, shown as the scope names
            themselves. */}
        <div className="stack-1" data-testid={`mcp-grant-read-scopes-${grant.id}`}>
          <span className="t-label">{dict.ui.aiConnections.readAccessLabel}</span>
          <span className="mono-meta">
            {authority.readScopes.length > 0 ? authority.readScopes.join(', ') : dict.ui.aiConnections.noScopes}
          </span>
        </div>

        <div className="stack-1" data-testid={`mcp-grant-write-scopes-${grant.id}`}>
          <span className="t-label">{dict.ui.aiConnections.writeAccessLabel}</span>
          <span className="mono-meta">
            {authority.writeScopes.length > 0 ? authority.writeScopes.join(', ') : dict.ui.aiConnections.noScopes}
          </span>
        </div>

        {/* 16.3 bullet 3: whether the grant includes approval, immediate
            publication, or automation control, in plain language rather
            than scope names alone -- a distinct block from the raw scope
            list just above, and the reason this screen calls write
            authority out separately at all (16.3's own closing sentence). */}
        <div className="stack-1" data-testid={`mcp-grant-write-authority-${grant.id}`}>
          <span className="t-label">{dict.ui.aiConnections.writeAuthorityLabel}</span>
          {authority.capabilities.length > 0
            ? (
              <ul className="stack-1" style={{ margin: 0, paddingLeft: 'var(--sp-4)' }}>
                {authority.capabilities.map((capability) => (
                  <li key={capability} className="t-body">{capabilityLabel(dict, capability)}</li>
                ))}
              </ul>
            )
            : <span className="t-body">{dict.ui.aiConnections.noWriteAccess}</span>}
        </div>

        {grant.state === 'active' && (
          showConfirm
            ? (
              // 16.5, on the screen: revoking does not delete or unpublish
              // anything the client created -- stated here, in the
              // confirmation itself, not only in a help paragraph, so
              // revocation is never mistaken for a content rollback.
              <div className="alert alert--danger" role="alertdialog" aria-label={dict.ui.aiConnections.revokeConfirmTitle}>
                <div className="stack-2">
                  <div className="alert__title">{dict.ui.aiConnections.revokeConfirmTitle}</div>
                  <div className="alert__body">{dict.ui.aiConnections.revokeConfirmBody(grant.client)}</div>
                  {revokeState.status === 'error' && (
                    <div className="field__error">{dict.ui.aiConnections.revokeFailed}</div>
                  )}
                  <div className="row" style={{ gap: 'var(--sp-2)' }}>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={revokeState.status === 'revoking'}
                      onClick={() => onConfirmRevoke(grant.id)}
                    >
                      {revokeState.status === 'revoking' ? dict.ui.aiConnections.revoking : dict.ui.aiConnections.revokeConfirmAction}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={revokeState.status === 'revoking'}
                      onClick={() => onCancelRevoke(grant.id)}
                    >
                      {dict.ui.aiConnections.revokeCancelAction}
                    </button>
                  </div>
                </div>
              </div>
            )
            : (
              <div>
                <button type="button" className="btn btn--danger" onClick={() => onStartRevoke(grant.id)}>
                  {dict.actions.revoke}
                </button>
              </div>
            )
        )}
      </div>
    </div>
  );
};

export const AiConnectionsRoute = (): React.JSX.Element => {
  const dict = useDictionary();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [revokeStates, setRevokeStates] = useState<Record<string, RevokeState>>({});
  const [copied, setCopied] = useState(false);

  const load = (): void => {
    setState({ status: 'loading' });
    fetchMcpGrants()
      .then((data) => setState({ status: 'ready', data }))
      .catch(() => setState({ status: 'error' }));
  };

  useEffect(load, []);

  const copyEndpoint = (endpoint: string): void => {
    const clipboard = typeof navigator === 'object' ? navigator.clipboard : undefined;
    void Promise.resolve(clipboard?.writeText(endpoint)).catch(() => {
      // Clipboard access can fail (permissions, insecure context, no
      // clipboard API at all in some test environments) -- the endpoint
      // text stays visible and selectable regardless.
    }).finally(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  const setRevokeState = (id: string, next: RevokeState): void => {
    setRevokeStates((prev) => ({ ...prev, [id]: next }));
  };

  const onStartRevoke = (id: string): void => setRevokeState(id, { status: 'confirming' });
  const onCancelRevoke = (id: string): void => setRevokeState(id, IDLE_REVOKE);

  // 16.4: revocation takes effect immediately once confirmed -- there is no
  // further step after this call resolves. The grant list is patched with
  // the server's own returned row (never assumed locally) so the screen's
  // "active"/"revoked" split reflects exactly what the server now believes.
  const onConfirmRevoke = (id: string): void => {
    setRevokeState(id, { status: 'revoking' });
    revokeMcpGrant(id)
      .then((res) => {
        setState((prev) => (prev.status === 'ready'
          ? { status: 'ready', data: { ...prev.data, grants: prev.data.grants.map((g) => (g.id === id ? res.grant : g)) } }
          : prev));
        setRevokeState(id, IDLE_REVOKE);
      })
      .catch(() => setRevokeState(id, { status: 'error' }));
  };

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <h1 className="t-title">{dict.ui.aiConnections.title}</h1>

      {state.status === 'loading' && <p className="t-meta">{dict.ui.aiConnections.loading}</p>}

      {state.status === 'error' && (
        <div className="alert alert--danger">
          <div className="stack-1">
            <div className="alert__title">{dict.ui.aiConnections.loadFailed}</div>
            <button type="button" className="btn" onClick={load}>{dict.ui.aiConnections.retry}</button>
          </div>
        </div>
      )}

      {state.status === 'ready' && (() => {
        const { active, revoked } = partitionGrants(state.data.grants);
        const hasAnyGrant = state.data.grants.length > 0;

        return (
          <>
            {/* 1. Server endpoint -- first, per 16.2. */}
            <div className="panel">
              <div className="panel__head"><h2 className="t-section">{dict.ui.aiConnections.endpointTitle}</h2></div>
              <div className="panel__body stack-2">
                <p className="t-body">{dict.ui.aiConnections.endpointBody}</p>
                <div className="row wrap" style={{ gap: 'var(--sp-3)' }}>
                  <code className="mono">{state.data.endpoint}</code>
                  <button type="button" className="btn" onClick={() => copyEndpoint(state.data.endpoint)}>
                    {copied ? dict.ui.aiConnections.copied : dict.ui.aiConnections.copyEndpoint}
                  </button>
                </div>
              </div>
            </div>

            {/* 2. Setup guidance. */}
            <div className="panel">
              <div className="panel__head"><h2 className="t-section">{dict.ui.aiConnections.setupTitle}</h2></div>
              <div className="panel__body">
                <p className="t-body">{dict.ui.aiConnections.setupBody}</p>
              </div>
            </div>

            {/* 3. Authorized clients. */}
            <div className="panel">
              <div className="panel__head"><h2 className="t-section">{dict.ui.aiConnections.clientsTitle}</h2></div>
              <div className="panel__body stack">
                {!hasAnyGrant
                  ? (
                    // 16.6: no authorized clients is a normal starting
                    // state, not an error -- rendered with `.empty`, never
                    // `.alert--danger`.
                    <div className="empty">
                      <div className="empty__title">{dict.ui.aiConnections.noClientsTitle}</div>
                      <div className="empty__body">{dict.ui.aiConnections.noClientsBody}</div>
                    </div>
                  )
                  : (
                    <>
                      <div className="stack-2" data-testid="mcp-grants-active">
                        <h3 className="t-label">{dict.ui.aiConnections.activeSectionTitle}</h3>
                        {active.length === 0
                          ? <p className="t-meta">{dict.ui.aiConnections.noActiveClients}</p>
                          : active.map((grant) => (
                            <GrantRow
                              key={grant.id}
                              dict={dict}
                              grant={grant}
                              revokeState={revokeStates[grant.id] ?? IDLE_REVOKE}
                              onStartRevoke={onStartRevoke}
                              onCancelRevoke={onCancelRevoke}
                              onConfirmRevoke={onConfirmRevoke}
                            />
                          ))}
                      </div>

                      <hr className="rule" />

                      <div className="stack-2" data-testid="mcp-grants-revoked">
                        <h3 className="t-label">{dict.ui.aiConnections.revokedSectionTitle}</h3>
                        {revoked.length === 0
                          ? <p className="t-meta">{dict.ui.aiConnections.noRevokedClients}</p>
                          : revoked.map((grant) => (
                            <GrantRow
                              key={grant.id}
                              dict={dict}
                              grant={grant}
                              revokeState={IDLE_REVOKE}
                              onStartRevoke={onStartRevoke}
                              onCancelRevoke={onCancelRevoke}
                              onConfirmRevoke={onConfirmRevoke}
                            />
                          ))}
                      </div>
                    </>
                  )}
              </div>
            </div>

            {/* 4. Scope reference -- last, per 16.2. Purely descriptive: it
                never labels a scope "dangerous" itself, since that
                classification (`writeScopes`) is computed server-side per
                grant, above. */}
            <div className="panel">
              <div className="panel__head"><h2 className="t-section">{dict.ui.aiConnections.scopeReferenceTitle}</h2></div>
              <div className="panel__body">
                <p className="t-body">{dict.ui.aiConnections.scopeReferenceBody}</p>
                <dl className="kv">
                  {ALL_SCOPES.map((scope) => (
                    <Fragment key={scope}>
                      <dt className="mono-meta">{scope}</dt>
                      <dd>{dict.ui.aiConnections.scopeDescriptions[SCOPE_DICT_KEY[scope]]}</dd>
                    </Fragment>
                  ))}
                </dl>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
};
